import {
  Box,
  Paper,
  Skeleton,
  TableContainer,
  Table,
  TableHead,
  TablePagination,
  TableRow,
  TableCell,
  TableBody,
  Alert,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";

import {
  RobotoTypography,
  RobotoTableCell,
  RobotoTableRow,
} from "@/shared/components";
import { FormattedDateTime } from "@/shared/datetime";
import { SecretRecord } from "@/shared/domain/secrets";

import { SecretValue } from "./SecretValue";

/**
 * SecretsTableProps is the interface for the props of the SecretsTable component.
 *
 * @param {SecretRecord[]} secrets - The list of secrets to display.
 * @param {number} totalCount - The total number of secrets.
 * @param {string | null} errorMsg - The error message to display.
 * @param {boolean} isLoading - Whether the table is loading.
 * @param {number} pageSize - The number of secrets to display per page.
 * @param {number} pageNumber - The current page number.
 * @param {function} onPageBackward - The function to call when the user clicks the backward button.
 * @param {function} onPageForward - The function to call when the user clicks the forward button.
 * @param {function} onPageSizeChange - The function to call when the user changes the page size.
 */
interface SecretsTableProps {
  onPageBackward: (newPage: number) => void;
  onPageForward: (newPage: number) => void;
  onPageSizeChange: (newSize: number) => void;
  errorMsg: string | null;
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  secrets: SecretRecord[];
  totalCount: number;
}

export const SecretsTable: React.FC<SecretsTableProps> = ({
  onPageBackward,
  onPageForward,
  onPageSizeChange,
  errorMsg,
  isLoading,
  pageNumber,
  pageSize,
  secrets,
  totalCount,
}) => {
  let tableContent = null;

  if (isLoading) {
    tableContent = (
      <RobotoTableRow>
        <RobotoTableCell>
          <Skeleton
            animation="wave"
            variant="rounded"
            height={30}
            sx={{ mt: 1, mb: 1 }}
          />
        </RobotoTableCell>

        <RobotoTableCell>
          <Skeleton
            animation="wave"
            variant="rounded"
            height={30}
            sx={{ mt: 1, mb: 1 }}
          />
        </RobotoTableCell>

        <RobotoTableCell>
          <Skeleton
            animation="wave"
            variant="rounded"
            height={30}
            sx={{ mt: 1, mb: 1 }}
          />
        </RobotoTableCell>
        <RobotoTableCell>
          <Skeleton
            animation="wave"
            variant="rounded"
            height={30}
            sx={{ mt: 1, mb: 1 }}
          />
        </RobotoTableCell>
      </RobotoTableRow>
    );
  } else if (errorMsg) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={4}>
          <Alert severity="error">{errorMsg}</Alert>
        </TableCell>
      </TableRow>
    );
  } else {
    tableContent =
      secrets.length > 0 ? (
        secrets.map((secret) => (
          <RobotoTableRow key={secret.name}>
            <RobotoTableCell>
              <Link to={`/secrets/${encodeURIComponent(secret.name)}`}>
                {secret.name}
              </Link>
            </RobotoTableCell>

            <RobotoTableCell>
              <SecretValue secretRecord={secret} />
            </RobotoTableCell>

            <RobotoTableCell>
              <FormattedDateTime value={secret.created} />
            </RobotoTableCell>
            <RobotoTableCell>
              <FormattedDateTime value={secret.modified} />
            </RobotoTableCell>
          </RobotoTableRow>
        ))
      ) : (
        <TableRow>
          <TableCell
            colSpan={4}
            sx={{
              height: "4rem",
              textAlign: "center",
            }}
          >
            <RobotoTypography variant="subtitle2">
              No secrets exist yet. Create one using the form above.
            </RobotoTypography>
          </TableCell>
        </TableRow>
      );
  }
  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <RobotoTableCell>Name</RobotoTableCell>
              <RobotoTableCell>Value</RobotoTableCell>
              <RobotoTableCell>Created</RobotoTableCell>
              <RobotoTableCell>Modified</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>{tableContent}</TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={Box}
        count={totalCount}
        page={pageNumber}
        rowsPerPage={pageSize}
        labelRowsPerPage={"Items per page:"}
        rowsPerPageOptions={[10, 25, 50]}
        onPageChange={(_event, newPage) => {
          if (newPage > pageNumber) {
            onPageForward(newPage);
          } else {
            onPageBackward(newPage);
          }
        }}
        onRowsPerPageChange={(event) => {
          onPageSizeChange(parseInt(event.target.value));
        }}
      />
    </>
  );
};
